import { ROUTES } from '@/routes/routes'
import { currentMonth } from '@/lib/transactionDateRangeFilter'
import { transactionsLink } from '@/lib/transactionsLink'
import { cn } from '@/lib/utils'
import { SectionLink } from './SectionLink'

export function MonthSummary({
  income,
  expenses,
  savingsRate,
  format,
  className,
}: {
  income: number
  expenses: number
  savingsRate: number | null
  format: (n: number) => string
  className?: string
}) {
  const saved = income - expenses
  const rate = savingsRate === null ? null : Math.round(savingsRate)

  let ring: string
  let label: string
  let sub: string
  let ringText: string
  let ariaLabel: string
  if (rate === null) {
    ring = 'conic-gradient(var(--fl-border) 0 100%)'
    label = '—'
    sub = 'no income yet'
    ringText = 'text-[var(--fl-text-3)]'
    ariaLabel = 'No income recorded this month'
  } else if (saved >= 0) {
    const arc = Math.min(Math.max(rate, 0), 100)
    ring = `conic-gradient(var(--fl-income) 0 ${arc}%, var(--fl-border) ${arc}% 100%)`
    label = `${rate}%`
    sub = 'saved'
    ringText = 'text-[var(--fl-income)]'
    ariaLabel = `Savings rate ${label}`
  } else {
    // A negative savings rate reads as noise; the share of income spent (over 100%) says how far over.
    const spentPct = Math.round((expenses / income) * 100)
    ring = 'conic-gradient(var(--fl-error) 0 100%)'
    label = `${spentPct}%`
    sub = 'spent'
    ringText = 'text-[var(--fl-error)]'
    ariaLabel = `Spent ${spentPct}% of income`
  }

  const savedTone =
    rate === null
      ? 'text-[var(--fl-text-3)]'
      : saved < 0
        ? 'text-[var(--fl-error)]'
        : 'text-[var(--fl-heading)]'

  return (
    <section className={className}>
      <SectionLink
        to={transactionsLink({ month: currentMonth() }, { back: ROUTES.APP.DASHBOARD })}
        label="This month"
      />
      <div className="mt-1 flex items-center gap-4 lg:mt-1.5 lg:gap-8">
        <div
          role="img"
          aria-label={ariaLabel}
          className="relative h-[84px] w-[84px] shrink-0 rounded-full lg:h-28 lg:w-28"
          style={{ background: ring }}
        >
          <div className="absolute inset-[10px] flex flex-col lg:inset-[13px] items-center justify-center rounded-full bg-[var(--fl-bg)]">
            <span
              className={cn(
                'font-serif text-xl font-bold leading-[22px] lg:text-[28px] lg:leading-[30px]',
                ringText
              )}
            >
              {label}
            </span>
            <span className="text-[11px] text-[var(--fl-text-2)] lg:mt-0.5 lg:text-xs">{sub}</span>
          </div>
        </div>
        <dl className="grid min-w-0 flex-1 grid-cols-3 gap-2 lg:flex lg:flex-col lg:gap-3">
          <Figure label="In" dot="bg-[var(--fl-income)]" tone="text-[var(--fl-income)]">
            {format(income)}
          </Figure>
          <Figure label="Out" dot="bg-[var(--fl-expense)]" tone="text-[var(--fl-expense)]">
            {format(expenses)}
          </Figure>
          <Figure label="Saved" tone={cn('font-bold', savedTone)}>
            {rate === null ? '—' : format(saved)}
          </Figure>
        </dl>
      </div>
    </section>
  )
}

function Figure({
  label,
  dot,
  tone,
  children,
}: {
  label: string
  dot?: string
  tone: string
  children: React.ReactNode
}) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-xs text-[var(--fl-text-2)]">
        {dot && <span className={cn('h-2 w-2 rounded-full', dot)} aria-hidden="true" />}
        {label}
      </dt>
      <dd className={cn('truncate text-[15px] font-semibold tabular-nums lg:text-lg', tone)}>
        {children}
      </dd>
    </div>
  )
}
